import { doc, setDoc, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// Aide schedule create/read/delete. Rules: isAnyAdmin() + aideScheduleInScope.
// The doc id is a fake aide uid (smoke_aide_*) so a real paraeducator's
// schedule is never touched, and it carries a single-campus schoolIds so the
// split-site delete guard (schoolIds.hasOnly(token)) resolves the same way as
// create for school-tier admins.

export default {
  id: 'aideSchedules',
  label: 'Aide schedule create/delete',
  description: 'aideSchedules lifecycle asserted against the rules for your role.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const schoolTier = Array.isArray(ctx.tokenSchoolIds)
    if (schoolTier && !ctx.schoolId) {
      step.block(
        'create aide schedule',
        'Select a school before probing school-scoped aide schedules'
      )
      return
    }

    const aideId = `smoke_aide_${ctx.runId.slice(-8)}`
    const ref = doc(db, 'aideSchedules', aideId)
    const expectWrite = RULE_EXPECTATIONS.aideScheduleWrite(role)
    const schoolId = ctx.schoolId || null

    const payload = {
      studentIds: [],
      __smokeTest: true,
      __smokeRunId: ctx.runId,
      createdAt: serverTimestamp(),
      ...(schoolId
        ? {
            schoolId,
            schoolIds: [schoolId],
            schools: { [schoolId]: { classAssignment: {}, directAssignment: [] } }
          }
        : {})
    }

    let created = false
    try {
      await step(`create aide schedule (expected: ${fmtExpected(expectWrite)})`, async () => {
        const { allowed } = await attempt(() => setDoc(ref, payload))
        created = allowed
        return allowed
          ? `${accessDetail(expectWrite, allowed)} — aideSchedules/${aideId}`
          : accessDetail(expectWrite, allowed)
      })

      if (!created) {
        step.skip('remaining lifecycle', 'Role cannot create — later steps skipped')
        return
      }

      await step('read aide schedule back (expected: allowed)', async () => {
        const { allowed } = await attempt(async () => {
          const snap = await getDoc(ref)
          if (!snap.exists()) throw new Error('Created aide schedule not found on read-back')
        })
        return accessDetail(true, allowed)
      })

      await step(`delete aide schedule (expected: ${fmtExpected(expectWrite)})`, async () => {
        const { allowed } = await attempt(() => deleteDoc(ref))
        return accessDetail(expectWrite, allowed)
      })
    } finally {
      await deleteDoc(ref).catch(() => {})
    }
  }
}
